import { useEffect, useRef } from "preact/hooks";
import { caretDown, close, newChat } from "../icons.ts";
import { API_ORIGIN } from "../lib/api.ts";
import { framed } from "../lib/bridge.ts";
import { attachSheetDrag } from "../lib/sheet-drag.ts";
import type { WidgetConfig } from "../lib/types.ts";
import { Icon } from "./Icon.tsx";
import styles from "./Header.module.css";

interface Props {
  config: WidgetConfig;
  canReset: boolean;
  scrolled: boolean;
  onReset(): void;
  onClose(): void;
}

export function Header({ config, canReset, scrolled, onReset, onClose }: Props) {
  const subtitle = config.widget.subtitle?.trim() || (config.mode === "shop" ? "Asystent sklepu" : "Asystent oferty");
  // On a phone the header is the sheet's handle: dragged down, the chat closes.
  const header = useRef<HTMLElement>(null);
  useEffect(() => (framed && header.current ? attachSheetDrag(header.current) : undefined), []);
  return (
    <header ref={header} class={styles.header} data-scrolled={scrolled || undefined}>
      {framed && <span class={styles.grabber} aria-hidden="true" />}
      <Avatar config={config} />
      <div class={styles.titles}>
        <h1 class={styles.name}>{config.tenant_name}</h1>
        <p class={styles.subtitle}>{subtitle}</p>
      </div>
      <div class={styles.actions}>
        {canReset && (
          <button type="button" class={styles.action} onClick={onReset} aria-label="Nowa rozmowa" title="Nowa rozmowa">
            <Icon svg={newChat} />
          </button>
        )}
        {framed && (
          <button type="button" class={styles.action} onClick={onClose} aria-label="Zamknij czat" title="Zamknij">
            <Icon svg={close} class={styles.panelOnly} />
            <Icon svg={caretDown} class={styles.fullscreenOnly} />
          </button>
        )}
      </div>
    </header>
  );
}

export function Avatar({ config, size = "md" }: { config: WidgetConfig; size?: "md" | "lg" }) {
  if (config.logo_url) {
    return <img class={styles.logo} data-size={size} src={`${API_ORIGIN}${config.logo_url}`} alt="" width={36} height={36} decoding="async" />;
  }
  const initial = config.tenant_name.trim().charAt(0).toUpperCase() || "A";
  return (
    <span class={styles.monogram} data-size={size} aria-hidden="true">
      {initial}
    </span>
  );
}
